import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useAlgo } from '../hooks/useAlgo';
import { useBalanceStore } from '../hooks/useBalanceStore';
import useJobCooldown from '../hooks/useJobCooldown';
import { useUserAssetsQuery } from '../hooks/useUserAssetsStore';
import FullScreenLoader from './FullScreenLoader';
import NftImage from './NftImage';

interface JobKittyDisplayProps {
  kitty: any;
  showEnergyDrink?: boolean;
}

export default function JobKittyDisplay({ kitty, showEnergyDrink = false }: JobKittyDisplayProps) {
  const { cooldown, loading, cooldownQuery } = useJobCooldown(kitty.id);
  const { energyDrinkBalance } = useBalanceStore();
  const algoContext = useAlgo();
  const { drinkEnergyDrinkForJob, address } = algoContext as any;
  const userAssetsQuery = useUserAssetsQuery({ address });

  const drinkEnergyDrinkMutation = useMutation({
    mutationFn: (variables: { kitty: any }) => drinkEnergyDrinkForJob ? drinkEnergyDrinkForJob(variables) : Promise.reject('Function not available'),
    onSuccess: () => {
      userAssetsQuery.refetch();
      cooldownQuery.refetch();
      toast.success('Shitty Energy Drink consumed');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'An error occurred');
    },
  });

  const onEnergyDrink = () => {
    if (!cooldown || !drinkEnergyDrinkForJob) return;
    drinkEnergyDrinkMutation.mutate({ kitty });
  };

  return (
    <>
      <FullScreenLoader loading={drinkEnergyDrinkMutation.isLoading} label='Drinking energy drink...'>
        <></>
      </FullScreenLoader>

      <div className='flex flex-col items-center space-y-1 relative'>
        <div className={clsx('w-16 h-16 rounded-lg overflow-hidden bg-gray-800 relative')}>
          <NftImage nft={kitty} width={64} height={64} className={clsx('w-full h-full object-cover', cooldown && 'grayscale')} />

          {/* Energy drink button overlay */}
          {cooldown && energyDrinkBalance > 0 && showEnergyDrink && drinkEnergyDrinkForJob && (
            <div className='absolute top-[4px] right-[4px]'>
              <button
                type='button'
                disabled={userAssetsQuery.isLoading}
                onClick={onEnergyDrink}
                className='relative hover:scale-110 transition-transform'
              >
                <img
                  src='/shitty-energy-drink.png'
                  alt='Energy Drink'
                  className='w-6 h-6 rounded-md'
                />
                <span className='sr-only'>Drink Energy Drink</span>
              </button>
            </div>
          )}
        </div>

        <div className={clsx('text-center', cooldown && 'opacity-50')}>
          <span className='text-xs text-neutral-400 text-center max-w-16 truncate block'>
            {kitty.bio?.bio?.name || kitty.name}
          </span>

          {cooldown && (
            <div className='text-xs text-red-400 mt-1'>
              <p className='whitespace-nowrap'>Cooldown until</p>
              <p className='font-medium'>{dayjs(cooldown).format('MM/D h:mm A')}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
